import { MedplumClient, WithId } from "@medplum/core";
import { DocumentReference } from "@medplum/fhirtypes";

import { captureExceptionWithoutPhi } from "@/utils/sentry";

import { DOT_PHRASE_ACTIVE_STATUS, DotPhraseScope } from "../constants/dot-phrases";
import {
  InsertableDotPhrase,
  LOAD_INSERTABLE_DOT_PHRASES_FAILURE,
  LoadInsertableDotPhrasesResult,
} from "../types/insertable-dot-phrase";
import {
  buildDotPhrasePreview,
  normalizeDotPhraseTrigger,
  parseDotPhraseDescription,
  readDotPhraseBodyHtml,
  readDotPhraseTrigger,
} from "../utils/dot-phrases";

import { buildScopeQuery } from "./scope-query";

export const INSERTABLE_DOT_PHRASE_LIMIT = 500;

const LOAD_FAILED_MESSAGE = "Failed to load dotphrases for in-note autocomplete";

function toInsertableDotPhrase(
  resource: WithId<DocumentReference>,
  scope: DotPhraseScope,
): InsertableDotPhrase | undefined {
  const trigger = readDotPhraseTrigger(resource);

  if (!resource.id || !trigger) return undefined;

  const { title, body } = parseDotPhraseDescription(resource.description ?? "");

  return {
    id: resource.id,
    trigger: normalizeDotPhraseTrigger(trigger),
    title,
    preview: buildDotPhrasePreview(body),
    bodyHtml: readDotPhraseBodyHtml(resource.content),
    scope,
  };
}

async function searchInsertableDotPhrases(
  medplum: MedplumClient,
  scope: DotPhraseScope,
  practiceId: string,
  practitionerId: string,
): Promise<InsertableDotPhrase[]> {
  const phrases = await medplum.searchResources("DocumentReference", {
    ...buildScopeQuery({ scope, practiceId, practitionerId }, DOT_PHRASE_ACTIVE_STATUS),
    _count: INSERTABLE_DOT_PHRASE_LIMIT.toString(),
  });

  return phrases.reduce<InsertableDotPhrase[]>((insertable, phrase) => {
    const mapped = toInsertableDotPhrase(phrase, scope);

    if (mapped) insertable.push(mapped);

    return insertable;
  }, []);
}

export interface LoadInsertableDotPhrasesParams {
  medplum: MedplumClient;
  practiceId: string | undefined;
  practitionerId: string | undefined;
}

export async function loadInsertableDotPhrases({
  medplum,
  practiceId,
  practitionerId,
}: LoadInsertableDotPhrasesParams): Promise<LoadInsertableDotPhrasesResult> {
  if (!practiceId) {
    return { ok: false, reason: LOAD_INSERTABLE_DOT_PHRASES_FAILURE.MissingPractice };
  }

  if (!practitionerId) {
    return { ok: false, reason: LOAD_INSERTABLE_DOT_PHRASES_FAILURE.MissingPractitioner };
  }

  try {
    const [shared, personal] = await Promise.all([
      searchInsertableDotPhrases(medplum, DotPhraseScope.Shared, practiceId, practitionerId),
      searchInsertableDotPhrases(medplum, DotPhraseScope.Personal, practiceId, practitionerId),
    ]);

    return {
      ok: true,
      data: [...shared, ...personal].sort((a, b) => a.trigger.localeCompare(b.trigger)),
    };
  } catch (error) {
    captureExceptionWithoutPhi(LOAD_FAILED_MESSAGE, error);

    return { ok: false, reason: LOAD_INSERTABLE_DOT_PHRASES_FAILURE.LoadFailed };
  }
}
